"use client";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@nyntax/design-system/components/ui/avatar";
import { Button } from "@nyntax/design-system/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@nyntax/design-system/components/ui/dropdown-menu";
import { useTheme } from "@nyntax/design-system/providers/theme";
import { i18nConfig } from "@nyntax/internationalization/config";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  LuBookOpen,
  LuCheck,
  LuGraduationCap,
  LuLanguages,
  LuLaptop,
  LuLayoutDashboard,
  LuLogOut,
  LuMoon,
  LuSun,
  LuSwatchBook,
  LuUserCog,
} from "react-icons/lu";

import { signOut } from "@/lib/auth";

/** Translated strings the account menu shows. */
export interface UserMenuLabels {
  account: string;
  instructor: string;
  language: string;
  languageBn: string;
  languageEn: string;
  menuLabel: string;
  myLearning: string;
  signOut: string;
  teachOnAcademy: string;
  theme: string;
  themeDark: string;
  themeLight: string;
  themeSystem: string;
}

interface UserMenuProps {
  email: string;
  /** The account photo, or null to show the initial. */
  image?: string | null;
  isInstructor: boolean;
  labels: UserMenuLabels;
  locale: string;
  name: string;
}

const initialOf = (name: string, email: string): string => {
  const source = name.trim() === "" ? email : name;

  return source.trim().charAt(0).toUpperCase();
};

/** The same path under another locale: `/en-US/library` becomes `/bn-BD/library`. */
const pathInLocale = (pathname: string, locale: string): string => {
  const segments = pathname.split("/");

  segments[1] = locale;

  return segments.join("/");
};

/**
 * The signed-in person's menu, Udemy style: where they study, where they
 * teach, account settings, language, theme and sign out.
 *
 * @param props - The person, the labels and the current locale.
 * @returns The account menu.
 */
export const UserMenu = ({
  email,
  image = null,
  isInstructor,
  labels,
  locale,
  name,
}: UserMenuProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { setTheme, theme } = useTheme();

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      router.push(`/${locale}`);
      router.refresh();
    }
  };

  const themes = [
    { icon: LuSun, label: labels.themeLight, value: "light" },
    { icon: LuMoon, label: labels.themeDark, value: "dark" },
    { icon: LuLaptop, label: labels.themeSystem, value: "system" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            aria-label={labels.menuLabel}
            className="rounded-full"
            size="icon-xl"
            variant="ghost"
          />
        }
      >
        <Avatar size="lg">
          {image === null ? null : <AvatarImage alt="" src={image} />}
          <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
            {initialOf(name, email)}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-64">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-3 py-2">
            <Avatar size="lg">
              {image === null ? null : <AvatarImage alt="" src={image} />}
              <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                {initialOf(name, email)}
              </AvatarFallback>
            </Avatar>
            <span className="flex min-w-0 flex-col">
              <span className="text-foreground truncate text-sm font-semibold">
                {name}
              </span>
              <span className="text-muted-foreground truncate text-xs font-normal">
                {email}
              </span>
            </span>
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem render={<Link href={`/${locale}/library`} />}>
            <LuBookOpen />
            {labels.myLearning}
          </DropdownMenuItem>
          <DropdownMenuItem render={<Link href={`/${locale}/teach`} />}>
            {isInstructor ? <LuLayoutDashboard /> : <LuGraduationCap />}
            {isInstructor ? labels.instructor : labels.teachOnAcademy}
          </DropdownMenuItem>
          <DropdownMenuItem render={<Link href={`/${locale}/account`} />}>
            <LuUserCog />
            {labels.account}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <LuLanguages />
              {labels.language}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {i18nConfig.locales.map((code) => (
                <DropdownMenuItem
                  key={code}
                  render={
                    <Link
                      href={pathInLocale(pathname, code)}
                      hrefLang={code}
                      lang={code}
                    />
                  }
                >
                  {code === "bn-BD" ? labels.languageBn : labels.languageEn}
                  {code === locale ? <LuCheck className="ml-auto" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <LuSwatchBook />
              {labels.theme}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {themes.map(({ icon: Icon, label, value }) => (
                <DropdownMenuItem
                  key={value}
                  onClick={() => {
                    setTheme(value);
                  }}
                >
                  <Icon />
                  {label}
                  {theme === value ? <LuCheck className="ml-auto" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} variant="destructive">
          <LuLogOut />
          {labels.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
